/**
 * Analyzer: Account Asymmetry (Cross-account divergence)
 *
 * When the same setup produces very different outcomes across accounts,
 * the problem isn't the setup — it's sizing, fills, or exit discipline
 * on one account. This is especially valuable for prop-firm accounts
 * where rules differ but the trade idea is shared.
 *
 * Requires 2+ accounts with non-trivial P&L divergence.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../types';
import { fmt$ } from '../utils';

export const key = 'accountAsymmetry';

export const analyze = (ctx) => {
  const { session, enriched, summary, getAccountById, hideAccounts = false } = ctx;
  if (!session || !Array.isArray(enriched) || enriched.length < 2) return null;

  const accountPnl = new Map();
  enriched.forEach(({ trade, netPnl }) => {
    // Renamed from `key` — the outer module exports `key = 'accountAsymmetry'`
    // and this used to shadow it inside the forEach. Worked by accident
    // (the closure already ended by the observation-id site below), but a
    // future refactor of either name would silently break.
    const accId = trade.accountId || 'unassigned';
    accountPnl.set(accId, (accountPnl.get(accId) || 0) + netPnl);
  });

  if (accountPnl.size < 2) return null;

  const rows = Array.from(accountPnl.entries())
    .map(([id, pnl]) => ({
      id,
      pnl,
      name: getAccountById?.(id)?.name || 'Unassigned'
    }))
    .sort((a, b) => b.pnl - a.pnl);

  const best = rows[0];
  const worst = rows[rows.length - 1];
  const divergence = Math.abs(best.pnl - worst.pnl);
  const totalAbs = Math.abs(summary.total || 0);

  // Only flag when best is positive, worst is negative, AND divergence
  // exceeds 50% of the session's absolute total (i.e. the split is real).
  if (best.pnl <= 0 || worst.pnl >= 0 || divergence <= totalAbs * 0.5) return null;

  const bestLabel = hideAccounts ? '••••••' : best.name;
  const worstLabel = hideAccounts ? '••••••' : worst.name;

  return makeObservation({
    id: `${key}:${session.id}:${best.id}-vs-${worst.id}`,
    analyzer: key,
    scope: 'session',
    scopeId: session.id,
    severity: SEVERITY.WARNING,
    category: CATEGORY.ACCOUNT,
    title: `Same setup, very different outcomes across accounts`,
    detail: `${bestLabel} ${fmt$(best.pnl)} vs ${worstLabel} ${fmt$(worst.pnl)} — sizing, fills, or exits diverged.`,
    action: 'Compare the fill times and exit rules on both accounts — the leak is procedural, not strategic.',
    evidence: { best, worst, rows, divergence },
    metric: { label: 'Divergence', value: divergence, unit: '$' },
    confidence: 0.76,
    priority: 72
  });
};
